import { useEffect, useState } from 'react';
import { Alert, Button, Card, Empty, Modal, Select, Spin, Statistic, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import dashboardApi from '../services/dashboardApi';
import { useAppSelector } from '../../../store/hooks';
import { initialQualityFacilityIds } from '../../../utils/facilityScope';
import type {
  QualityDashboardData,
  QualityAlertDetail,
  QualityAlertDetailsResponse,
  QualityMetricRow,
} from '../types/dashboard.types';
import './QualityDashboard.css';

interface QualityDashboardProps {
  dateRange: {
    start: string;
    end: string;
  };
}

interface BreakdownProps {
  title: string;
  rows: QualityMetricRow[];
  labelKey: keyof QualityMetricRow;
  idKey?: 'provider_id' | 'alert_prompt_id' | 'attending_source_key';
  onSelect?: (row: QualityMetricRow, label: string) => void;
}

interface DrilldownSelection {
  type: 'provider' | 'alert' | 'attending_source';
  id: number | string;
  label: string;
}

interface ComparisonCardProps {
  label: string;
  current: number;
  previous: number;
  format: 'count' | 'percent' | 'hours';
  favorableDirection?: 'higher' | 'lower';
}

const summaryCards: Array<{
  key: keyof QualityDashboardData['summary'];
  label: string;
  suffix?: string;
  precision?: number;
  tone: string;
}> = [
  { key: 'charts_evaluated', label: 'Charts Evaluated', tone: 'ink' },
  { key: 'charts_flagged_percent', label: 'Charts Flagged', suffix: '%', precision: 1, tone: 'gold' },
  { key: 'attending_notification_rate', label: 'Attending Notified', suffix: '%', precision: 1, tone: 'gold' },
];

function Breakdown({ title, rows, labelKey, idKey, onSelect }: BreakdownProps) {
  const maximum = Math.max(...rows.map((row) => row.count), 1);

  return (
    <Card className="quality-breakdown" title={title}>
      {rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No flags in this period" />
      ) : (
        <div className="quality-breakdown__rows">
          {rows.slice(0, 8).map((row) => {
            const label = String(row[labelKey] ?? 'Unknown');
            return (
              <div
                className={`quality-breakdown__row${onSelect ? ' quality-breakdown__row--interactive' : ''}`}
                key={`${String(idKey ? row[idKey] : label)}-${label}`}
              >
                {onSelect ? (
                  <button
                    type="button"
                    className="quality-breakdown__button"
                    onClick={() => onSelect(row, label)}
                    aria-label={`View ${row.count} alerts for ${label}`}
                  >
                    <span>{label}</span>
                    <strong>{row.count}</strong>
                  </button>
                ) : (
                  <div className="quality-breakdown__label">
                    <span>{label}</span>
                    <strong>{row.count}</strong>
                  </div>
                )}
                <div className="quality-breakdown__track" aria-hidden="true">
                  <span style={{ width: `${(row.count / maximum) * 100}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function ComparisonCard({
  label,
  current,
  previous,
  format,
  favorableDirection,
}: ComparisonCardProps) {
  const delta = roundMetric(current - previous);
  const valueSuffix = format === 'percent' ? '%' : format === 'hours' ? ' hrs' : '';
  const deltaSuffix = format === 'percent' ? ' pts' : format === 'hours' ? ' hrs' : '';
  const deltaLabel = previous === 0 && current > 0 && format === 'count'
    ? 'New activity'
    : `${delta > 0 ? '+' : ''}${delta}${deltaSuffix}`;
  const trendClass = delta === 0 || !favorableDirection
    ? ''
    : (delta > 0) === (favorableDirection === 'higher')
      ? 'is-favorable'
      : 'is-concerning';

  return (
    <article className="quality-comparison__metric">
      <span>{label}</span>
      <strong>{roundMetric(current)}{valueSuffix}</strong>
      <small className={trendClass}>
        {deltaLabel} vs. prior period
      </small>
    </article>
  );
}

function roundMetric(value: number) {
  return Math.round(value * 10) / 10;
}

function formatPeriod(start: string, end: string) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return `${formatter.format(new Date(start))} – ${formatter.format(new Date(end))}`;
}

export default function QualityDashboard({ dateRange }: QualityDashboardProps) {
  const scopeMode = useAppSelector((state) => state.facilities.scopeMode);
  const sessionFacilityId = useAppSelector((state) => state.facilities.selectedFacilityId);
  const globalFacilities = useAppSelector((state) => state.facilities.facilities);

  // The facility the user signed in to is where the dashboard starts (issue #243). Resolved
  // once, on mount: changing facility afterwards goes through the login selector, which
  // reloads the page. An empty list still means "every active facility" to the API.
  const [initialFacilityIds] = useState<number[]>(
    () => initialQualityFacilityIds(scopeMode, sessionFacilityId),
  );

  const [data, setData] = useState<QualityDashboardData | null>(null);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<number[]>(initialFacilityIds);
  const [selectedAlertPromptIds, setSelectedAlertPromptIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<DrilldownSelection | null>(null);
  const [drilldownData, setDrilldownData] = useState<QualityAlertDetailsResponse | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState<string | null>(null);
  const [expandedDetailIds, setExpandedDetailIds] = useState<number[]>([]);

  const loadDashboard = async (
    facilityIds = selectedFacilityIds,
    alertPromptIds = selectedAlertPromptIds,
  ) => {
    setDrilldown(null);
    setDrilldownData(null);
    setLoading(true);
    setError(null);
    try {
      setData(
        await dashboardApi.getQualityDashboard(
          dateRange,
          facilityIds,
          alertPromptIds,
        ),
      );
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Unable to load quality dashboard data.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard(initialFacilityIds, []);
    // Date changes are applied when the executive selects Refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDrilldown = async (selection: DrilldownSelection, page = 1) => {
    if (!data) return;

    setDrilldown(selection);
    setDrilldownData(null);
    setExpandedDetailIds([]);
    setDrilldownLoading(true);
    setDrilldownError(null);
    try {
      setDrilldownData(await dashboardApi.getQualityDashboardDetails({
        dateRange: data.date_range,
        facilityIds: data.selected_facility_ids,
        alertPromptIds: data.selected_alert_prompt_ids,
        drilldownType: selection.type,
        drilldownId: selection.id,
        page,
      }));
    } catch (requestError) {
      setDrilldownError(
        requestError instanceof Error
          ? requestError.message
          : 'Unable to load alert details.',
      );
    } finally {
      setDrilldownLoading(false);
    }
  };

  const detailColumns: ColumnsType<QualityAlertDetail> = [
    {
      title: 'Flagged',
      dataIndex: 'flagged_at',
      width: 165,
      render: (value: string) => new Intl.DateTimeFormat('en-US', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value)),
    },
    { title: 'Alert', dataIndex: 'alert_type', width: 190 },
    {
      title: 'Patient / Encounter',
      key: 'patient',
      width: 190,
      render: (_, row) => (
        <div>
          <strong>{row.patient || 'Unknown patient'}</strong>
          <div className="quality-drilldown__secondary">{row.encounter_number}</div>
        </div>
      ),
    },
    { title: 'Facility', dataIndex: 'facility', width: 165 },
    { title: 'Provider', dataIndex: 'provider', width: 165 },
    {
      title: 'Severity',
      dataIndex: 'severity',
      width: 100,
      render: (value: string) => (
        <Tag className={`quality-drilldown__severity quality-drilldown__severity--${value.toLowerCase()}`}>
          {value}
        </Tag>
      ),
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      width: 105,
      render: (value: number) => `${Math.round(value * 100)}%`,
    },
    { title: 'Status', dataIndex: 'status', width: 125 },
    {
      title: 'AI Finding',
      dataIndex: 'flag_reason',
      width: 280,
      render: (value: string, row) => (
        <div className="quality-drilldown__finding-cell">
          <span className="quality-drilldown__finding" title={value || undefined}>
            {value || 'No finding details recorded.'}
          </span>
          <button
            type="button"
            className="quality-drilldown__finding-toggle"
            aria-expanded={expandedDetailIds.includes(row.id)}
            onClick={() => setExpandedDetailIds((current) => (
              current.includes(row.id)
                ? current.filter((id) => id !== row.id)
                : [...current, row.id]
            ))}
          >
            {expandedDetailIds.includes(row.id) ? 'Hide full details' : 'View full details'}
          </button>
        </div>
      ),
    },
  ];

  return (
    <section className="quality-dashboard" aria-labelledby="quality-dashboard-title">
      <header className="quality-dashboard__hero">
        <div>
          <p className="quality-dashboard__eyebrow">Clinical quality intelligence</p>
          <h1 id="quality-dashboard-title">Quality Executive Dashboard</h1>
          <p>
            Tenant-wide AI chart review performance, review completion, and signal quality.
          </p>
        </div>
        <div className="quality-dashboard__filters">
          <div className="quality-dashboard__filter-field">
            <label htmlFor="quality-facilities">Facilities</label>
            <Select
              id="quality-facilities"
              mode="multiple"
              maxTagCount="responsive"
              allowClear
              showSearch
              optionFilterProp="label"
              value={selectedFacilityIds}
              options={(data?.facilities?.length ? data.facilities : globalFacilities).map((facility) => ({
                value: facility.id,
                label: facility.name,
              }))}
              placeholder="All facilities"
              onChange={setSelectedFacilityIds}
              aria-label="Quality dashboard facilities"
            />
          </div>
          <div className="quality-dashboard__filter-field">
            <label htmlFor="quality-alert-prompts">Parameter</label>
            <Select
              id="quality-alert-prompts"
              mode="multiple"
              maxTagCount="responsive"
              allowClear
              showSearch
              optionFilterProp="label"
              value={selectedAlertPromptIds}
              options={(data?.alert_prompts ?? []).map((prompt) => ({
                value: prompt.id,
                label: prompt.label,
              }))}
              placeholder="All Natural Language Alerts"
              onChange={setSelectedAlertPromptIds}
              aria-label="Quality dashboard Natural Language Alerts"
            />
          </div>
          <Button type="primary" onClick={() => loadDashboard()} loading={loading}>
            Refresh
          </Button>
        </div>
      </header>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Quality dashboard unavailable"
          description={error}
        />
      )}

      {loading && !data ? (
        <div className="quality-dashboard__loading">
          <Spin size="large" tip="Loading quality trends..." />
        </div>
      ) : data ? (
        <>
          <div className="quality-summary-grid">
            {summaryCards.map((card) => (
              <Card className={`quality-summary-card quality-summary-card--${card.tone}`} key={card.key}>
                <Statistic
                  title={card.label}
                  value={data.summary[card.key]}
                  precision={card.precision}
                  suffix={card.suffix}
                />
              </Card>
            ))}
          </div>

          <Card
            className="quality-comparison"
            title="Change from Previous Period"
            extra={`Previous period: ${formatPeriod(
              data.comparison.previous_date_range.start,
              data.comparison.previous_date_range.end,
            )}`}
          >
            <div className="quality-comparison__grid">
              <ComparisonCard
                label="Flags Created"
                current={data.summary.flags_created}
                previous={data.comparison.previous_summary.flags_created}
                format="count"
              />
              <ComparisonCard
                label="Charts Flagged"
                current={data.summary.charts_flagged_percent}
                previous={data.comparison.previous_summary.charts_flagged_percent}
                format="percent"
                favorableDirection="lower"
              />
            </div>
          </Card>

          <div className="quality-chart-grid">
            <Card title="Evaluations and Flags" className="quality-chart-card">
              {data.activity_by_day.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={data.activity_by_day}>
                    <defs>
                      <linearGradient id="qualityEvaluated" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#424349" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#424349" stopOpacity={0.03} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="evaluated"
                      name="Charts evaluated"
                      stroke="#424349"
                      fill="url(#qualityEvaluated)"
                    />
                    <Line
                      type="monotone"
                      dataKey="flagged"
                      name="Flags created"
                      stroke="#e4311a"
                      strokeWidth={3}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No chart review activity in this period" />
              )}
            </Card>

            <Card
              title="Percentage of Charts Flagged"
              className="quality-chart-card"
              extra={`${data.summary.charts_flagged_percent.toFixed(1)}% overall`}
            >
              {data.activity_by_day.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.activity_by_day}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} unit="%" />
                    <Tooltip
                      formatter={(value) => [`${value}%`, 'Charts flagged']}
                      labelFormatter={(date) => `Date: ${date}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="charts_flagged_percent"
                      name="Charts flagged"
                      stroke="#e4311a"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No chart evaluations in this period" />
              )}
            </Card>
          </div>

          <div className="quality-breakdown-grid">
            <Breakdown
              title="Alerts in Selected Scope"
              rows={data.by_prompt}
              labelKey="alert_type"
              idKey="alert_prompt_id"
              onSelect={(row, label) => loadDrilldown({
                type: 'alert',
                id: row.alert_prompt_id as number,
                label,
              })}
            />
            <Card
              className="quality-breakdown quality-provider-outliers"
              title="Provider Outliers"
              extra="10+ evaluations; 5+ points above baseline"
            >
              {data.provider_outliers.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No provider outliers in this period"
                />
              ) : (
                <div className="quality-provider-outliers__rows">
                  {[...data.provider_outliers]
                    .sort((left, right) => (right.rate_delta ?? 0) - (left.rate_delta ?? 0))
                    .slice(0, 8)
                    .map((row) => (
                      <button
                        type="button"
                        className="quality-provider-outliers__row"
                        key={row.provider_id ?? 'unassigned'}
                        onClick={() => loadDrilldown({
                          type: 'provider',
                          id: row.provider_id ?? 'unassigned',
                          label: row.provider ?? 'Unassigned',
                        })}
                        aria-label={`View alerts for provider ${row.provider ?? 'Unassigned'}`}
                      >
                        <span>
                          <strong>{row.provider ?? 'Unassigned'}</strong>
                          <small>{row.evaluated} evaluations · {row.count} flags</small>
                        </span>
                        <span className="quality-provider-outliers__rate">
                          <strong>{row.alert_rate}%</strong>
                          <small>+{row.rate_delta} pts</small>
                        </span>
                      </button>
                    ))}
                </div>
              )}
            </Card>
            <Breakdown
              title="Providers"
              rows={data.by_provider}
              labelKey="provider"
              idKey="provider_id"
              onSelect={(row, label) => loadDrilldown({
                type: 'provider',
                id: row.provider_id ?? 'unassigned',
                label,
              })}
            />
            <Breakdown
              title="Attending Source"
              rows={data.by_attending_source}
              labelKey="attending_source"
              idKey="attending_source_key"
              onSelect={(row, label) => loadDrilldown({
                type: 'attending_source',
                id: row.attending_source_key ?? 'not_attempted',
                label,
              })}
            />
          </div>
        </>
      ) : null}

      <Modal
        open={Boolean(drilldown)}
        title={drilldown ? `${drilldown.type === 'provider' ? 'Provider' : drilldown.type === 'alert' ? 'Alert' : 'Attending Source'}: ${drilldown.label}` : 'Alert details'}
        width="min(1380px, 96vw)"
        footer={null}
        destroyOnClose
        onCancel={() => {
          setDrilldown(null);
          setDrilldownData(null);
          setDrilldownError(null);
          setExpandedDetailIds([]);
        }}
      >
        <p className="quality-drilldown__context">
          Results reflect the facilities, Natural Language Alerts, and timeframe applied to the dashboard.
        </p>
        {drilldownError && (
          <Alert type="error" showIcon message="Alert details unavailable" description={drilldownError} />
        )}
        <Table<QualityAlertDetail>
          className="quality-drilldown__table"
          rowKey="id"
          columns={detailColumns}
          dataSource={drilldownData?.results ?? []}
          loading={drilldownLoading}
          scroll={{ x: 1490 }}
          locale={{ emptyText: 'No alerts match the applied dashboard filters.' }}
          pagination={drilldownData ? {
            current: drilldownData.pagination.page,
            pageSize: drilldownData.pagination.page_size,
            total: drilldownData.pagination.total,
            showSizeChanger: false,
            showTotal: (total) => `${total} alert${total === 1 ? '' : 's'}`,
            onChange: (page) => {
              if (drilldown) loadDrilldown(drilldown, page);
            },
          } : false}
          expandable={{
            expandedRowKeys: expandedDetailIds,
            showExpandColumn: false,
            expandedRowRender: (row) => (
              <div className="quality-drilldown__details">
                <section>
                  <h4>Flag reason</h4>
                  <p>{row.flag_reason || 'No reason recorded.'}</p>
                  {row.specific_concerns.length > 0 && (
                    <>
                      <h4>Specific concerns</h4>
                      <ul>
                        {row.specific_concerns.map((concern, index) => (
                          <li key={`${row.id}-${index}`}>{concern}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </section>
                <dl>
                  <div><dt>Attending notified</dt><dd>{row.attending_notified ? 'Yes' : 'No'}</dd></div>
                  <div><dt>Attending identified</dt><dd>{row.attending_name_from_chart || 'Not identified in chart'}</dd></div>
                  <div><dt>Notification source</dt><dd>{row.attending_source}</dd></div>
                  <div><dt>Notified at</dt><dd>{row.attending_notified_at ? new Date(row.attending_notified_at).toLocaleString() : 'Not sent'}</dd></div>
                  <div><dt>Reviewed by</dt><dd>{row.reviewed_by || 'Not reviewed'}</dd></div>
                  <div><dt>Reviewed at</dt><dd>{row.reviewed_at ? new Date(row.reviewed_at).toLocaleString() : 'Not reviewed'}</dd></div>
                  <div><dt>False positive</dt><dd>{row.is_false_positive ? 'Yes' : 'No'}</dd></div>
                  <div><dt>Sent in summary</dt><dd>{row.sent_in_summary_at ? new Date(row.sent_in_summary_at).toLocaleString() : 'No'}</dd></div>
                </dl>
              </div>
            ),
          }}
        />
      </Modal>
    </section>
  );
}
